import { Link } from "react-router-dom";
import { ArrowRight, Compass, Download, LayoutDashboard, RefreshCw, UsersRound } from "lucide-react";
import { API } from "@/lib/api";
import { Bar } from "@/components/ResultView";
import { Heading, SampleTag, DISC_LABEL } from "@/components/sample/SampleSections";

const MatchCard = ({ m, i }) => {
  const top = i === 0;
  const sub = top ? "text-white/70" : "text-muted-ink";
  return (
    <div className={`rounded-2xl p-6 lift flex flex-col ${top ? "bg-navy text-white md:col-span-2" : "bg-paper"}`} data-testid={`sample-match-${i}`}>
      <div className="flex items-baseline justify-between">
        <span className={`font-mono-dt text-xs ${top ? "text-[#8FD3C6]" : "text-teal"}`}>#{i + 1}</span>
        <span className={`font-display text-2xl font-semibold ${top ? "text-[#8FD3C6]" : "text-teal"}`} data-testid={`sample-match-score-${i}`}>{m.score}%</span>
      </div>
      <h3 className="font-display text-xl font-semibold mt-3">{m.name}</h3>
      <div className="mt-3"><Bar value={m.score} /></div>
      <p className={`text-sm mt-4 leading-relaxed ${sub}`}>{m.description}</p>
      <p className={`text-sm mt-3 leading-relaxed ${top ? "text-white/90" : "text-navy"}`}><strong>Why Jordan matches:</strong> {m.why}</p>
      <p className={`text-xs mt-4 ${sub}`}>Personality strengths: {m.why_disc.map((k) => `${DISC_LABEL[k]} (${k})`).join(", ")}</p>
      <p className={`text-xs mt-1.5 ${sub}`}>Spiritual gifts: {m.why_gifts.join(", ")}</p>
    </div>
  );
};

export const Matches = ({ v }) => (
  <section className="card-soft p-6 sm:p-8 md:p-10" data-testid="sample-matches">
    <div className="flex flex-wrap items-end justify-between gap-4">
      <Heading eyebrow="Ministry matching" title="Your Top 5 Ministry Matches" sub="Discover where your personality and spiritual gifts may help you make the greatest impact." />
      <SampleTag>Illustrative scores</SampleTag>
    </div>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mt-8">{v.matches.map((m, i) => <MatchCard key={m.team_id} m={m} i={i} />)}</div>
    <p className="text-xs text-muted-ink mt-6" data-testid="sample-matches-note">These matches are illustrative. Real results are calculated by the Dream Teams matching algorithm using each church's own active ministry teams.</p>
  </section>
);

export const ServingProfile = ({ v }) => (
  <section className="bg-navy text-white rounded-2xl p-6 sm:p-8 md:p-10" data-testid="sample-profile">
    <Heading light eyebrow="Personal ministry profile" title="Your Unique Serving Profile" />
    <div className="grid md:grid-cols-2 gap-x-10 gap-y-7 mt-8">
      {v.profile.map(([t, b], i) => (
        <div key={t} className={i === 0 ? "md:col-span-2" : ""} data-testid={`sample-profile-${i}`}>
          <p className="font-display text-lg font-semibold text-[#8FD3C6]">{t}</p>
          <p className="text-white/80 leading-relaxed mt-2">{b}</p>
        </div>
      ))}
    </div>
    <div className="mt-10 pt-8 border-t border-white/15 flex flex-wrap items-center justify-between gap-4">
      <p className="text-white/70 text-sm max-w-md">Download Jordan's full sample report to see the PDF your volunteers and church leaders receive.</p>
      <a href={`${API}/sample-results/pdf`} className="btn-teal inline-flex items-center gap-2" data-testid="sample-download-pdf-btn"><Download size={18} />Download Sample PDF</a>
    </div>
  </section>
);

const FEATURES = [
  [LayoutDashboard, "Volunteer Profiles", "View assessment results in one organized dashboard.", "Basic & Pro"],
  [Compass, "Ministry Matching", "Discover the teams where volunteers may thrive, based on your church's own ministries.", "Basic & Pro"],
  [UsersRound, "Volunteer Management", "Organize, review, and follow up with potential team members.", "Basic & Pro"],
  [RefreshCw, "Planning Center Integration", "Connect volunteer information with Planning Center People.", "Pro plan only"],
];

export const AdminExperience = () => (
  <section className="max-w-6xl mx-auto px-5 sm:px-6 py-20 md:py-28" data-testid="sample-admin">
    <Heading eyebrow="For church leaders" title={<>From Assessment to <span className="font-accent text-teal">Action</span></>}
      sub="Dream Teams doesn't just help volunteers discover their strengths. It helps church leaders understand their people and connect them with meaningful opportunities to serve." />
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mt-10">
      {FEATURES.map(([Icon, t, b, plan], i) => (
        <div key={t} className="card-soft p-6 lift" data-testid={`sample-admin-feature-${i}`}>
          <Icon size={22} className="text-teal" />
          <h3 className="font-display text-navy text-lg font-semibold mt-4">{t}</h3>
          <p className="text-sm text-muted-ink leading-relaxed mt-2">{b}</p>
          <p className="text-[11px] uppercase tracking-[0.12em] text-teal font-semibold mt-4" data-testid={`sample-admin-plan-${i}`}>{plan}</p>
        </div>
      ))}
    </div>
  </section>
);

export const Conversion = () => (
  <section className="max-w-6xl mx-auto px-5 sm:px-6 pb-20 md:pb-28" data-testid="sample-cta">
    <div className="rounded-[28px] bg-teal text-white p-8 sm:p-12 md:p-16 grain relative overflow-hidden">
      <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight max-w-2xl">Your Church Is Full of Untapped Potential.</h2>
      <p className="text-white/85 text-base md:text-lg leading-relaxed mt-5 max-w-xl">Discover the strengths of the people in your church and help every volunteer find a meaningful place to serve.</p>
      <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mt-10">
        <Link to="/#pricing" className="btn-ghost !text-navy inline-flex items-center justify-center gap-2" data-testid="sample-cta-get-started-btn">Get Started with Dream Teams <ArrowRight size={18} /></Link>
        <Link to="/free-assessment" className="btn-navy text-center" data-testid="sample-cta-free-assessment-btn">Take the Free DISC Assessment</Link>
      </div>
    </div>
  </section>
);
